import React, { useState, useEffect, Fragment } from 'react'

import Map from '@components/Map'

import ValuationService from '@services/valuation'
import MapService from '@services/map'

import { formatValuationParamsString } from '@utils/formatValuationStrings'
import { formatToLocale } from '@utils/numberFormatter'
import { getAllCategoriesAsArray } from '@utils/category'

import {
  RatingBody,
  ImageContainer,
  MapImgContainer,
  StreetViewContainer,
  Main,
  Container,
  Header,
  AddressContainer,
  AddressWrapper,
  Text,
  AreaWrapper,
  Download,
  SectionTitle,
  Section,
  InfoWrapper,
  InfoRow,
  InfoColumn,
  DataSection,
  IconWrapper,
  TitleWrapper,
  TimeMedianContainer,
  Time,
  TimeWrapper,
  MeasuresContainer,
  Measure,
  Wrapper,
  RatingWrapper,
  RatingIconWrapper,
  RatingHeader,
  RatingRank,
  Needle,
  Table,
  Row,
  Col,
  SuggestedPriceContainer,
  RatingFooter,
} from './styles'

import { Download as DownloadIcon, Statistics, Building, Rating } from '@icons'

const ValuationReport = props => {
  const [print, setPrint] = useState(undefined)
  const [valuation, setValuation] = useState(undefined)
  const [staticMapUrl, setStaticMapUrl] = useState('')
  const [mapService, setMapService] = useState(undefined)
  const [panorama, setPanorama] = useState(null)
  const categories = getAllCategoriesAsArray()

  useEffect(() => {
    const valuationService = new ValuationService()
    const { valuationId } = props.match.params
    valuationService.getValuationById(valuationId).then(valuation => {
      setValuation(valuation)
    })
  }, [props.match.params])

  useEffect(() => {
    if (mapService) {
      initializeStreetView()
      initializeStaticMap()
    }
  }, [mapService])

  useEffect(() => {
    if (print !== undefined) {
      mapService.getMaps().event.trigger(panorama, 'resize')
    }
  }, [print])

  const initializeStreetView = () => {
    const fenway = {
      lat: valuation.lat_lng[0],
      lng: valuation.lat_lng[1],
    }
    const panorama = mapService?.initializeStreetView(fenway, 'streetView')
    setPanorama(panorama)
  }

  const initializeStaticMap = () => {
    const pinLatLngString = valuation.lat_lng.toString()
    const path = valuation.geometries && valuation.geometries[0]
    let poly
    let encodedPath
    let polyCenter
    if (path) {
      poly = mapService?.createPolygon(
        path.map(x => ({ lat: x[0], lng: x[1] })),
      )
      const center = poly.getBounds().getCenter()
      polyCenter = `${center.lat()}, ${center.lng()}`
      encodedPath = `&path=fillcolor:0x6F798718%7Ccolor:0x6F7987%7Cenc:${mapService?.encodePath(
        poly.getPath(),
      )}`
    } else {
      const center = {
        lat: valuation.lat_lng[0],
        lng: valuation.lat_lng[1],
      }
      const circlePath = mapService?.createCircle(
        center,
        valuation.radius,
        'getPath',
      )
      encodedPath = `&path=fillcolor:0x6F798718%7Ccolor:0x6F7987%7Cenc:${mapService?.encodePath(
        circlePath,
      )}`
    }
    const url = `https://maps.googleapis.com/maps/api/staticmap?center=${polyCenter ||
      pinLatLngString}&zoom=${
      path ? 13 : 15
    }&scale=1&size=640x270&markers=color:red%7Clabel:%7C${pinLatLngString}${encodedPath}&key=${
      process.env.REACT_APP_GOOGLE_MAP_KEY
    }`

    setStaticMapUrl(url)
  }

  const initializeMapService = google => {
    const service = new MapService(google)
    setMapService(service)
  }

  const downloadPDF = () => {
    setPrint(true)
    setTimeout(() => {
      window.print()
    }, 500)
    setTimeout(() => {
      setPrint(false)
    }, 1000)
  }

  let subCategory

  if (valuation && valuation.main_sub_category) {
    subCategory =
      valuation &&
      categories.find(x => x.name === valuation.main_sub_category).prettyName
  } else {
    subCategory =
      valuation &&
      (isFinite(valuation.categories[0])
        ? valuation.categories.map(
            item =>
              categories.find(x => Number(x.value) === Number(item)).prettyName,
          )
        : valuation.categories
      ).join(', ')
  }

  return valuation ? (
    <Main print={print}>
      <Container print={print}>
        {print && <Header print={print}>Relatório do imóvel</Header>}
        {print && (
          <AddressContainer print={print}>
            <AddressWrapper print={print}>
              <Text dark bold>
                {valuation.fullAddress}
              </Text>
              <Text>{subCategory} - Venda</Text>
            </AddressWrapper>
            <AreaWrapper print={print}>
              <Text>
                Área mínima:{' '}
                {valuation.area -
                  (valuation.area * valuation.area_percent_margin) / 100}
                m²
              </Text>
              <Text>
                Área máxima:{' '}
                {valuation.area +
                  (valuation.area * valuation.area_percent_margin) / 100}
                m²
              </Text>
            </AreaWrapper>
          </AddressContainer>
        )}
        <ImageContainer>
          <MapImgContainer print={print} src={staticMapUrl} alt="static map" />
          <StreetViewContainer print={print} id="streetView">
            <Map
              useMapsApi
              onGoogleApiLoaded={initializeMapService}
              center={{ lat: 0, lng: 0 }}
              zoom={4}
            />
          </StreetViewContainer>
        </ImageContainer>
        {!print && <Header>Relatório do imóvel</Header>}
        {!print && (
          <AddressContainer>
            <AddressWrapper>
              <Text dark bold>
                {valuation.fullAddress}
              </Text>
              <Text>{subCategory} - Venda</Text>
            </AddressWrapper>
            <AreaWrapper>
              <Text>
                Área mínima:{' '}
                {valuation.area -
                  (valuation.area * valuation.area_percent_margin) / 100}
                m²
              </Text>
              <Text>
                Área máxima:{' '}
                {valuation.area +
                  (valuation.area * valuation.area_percent_margin) / 100}
                m²
              </Text>
            </AreaWrapper>
            <Download type="button" onClick={downloadPDF}>
              <DownloadIcon />
              Download em PDF
            </Download>
          </AddressContainer>
        )}
        <DataSection>
          <Section print={print}>
            <TitleWrapper print={print}>
              <IconWrapper print={print}>
                <Statistics
                  width={print ? 14 : null}
                  height={print ? 11 : null}
                />
              </IconWrapper>
              <SectionTitle>Estatísticas da região</SectionTitle>
            </TitleWrapper>
            <InfoWrapper print={print}>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Amostra
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text bold={print} print={print}>
                    {valuation.sample_size}
                  </Text>
                </InfoColumn>
              </InfoRow>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Média m² (100m)
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text bold={print} print={print}>
                    R${' '}
                    {formatToLocale(
                      valuation.price_avg_by_distance.find(
                        x => x.distance === 100,
                      ).price,
                    )}
                  </Text>
                </InfoColumn>
              </InfoRow>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Média m² (1000m)
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text bold={print} print={print}>
                    {`R$ ${formatToLocale(
                      valuation.price_avg_by_distance.find(
                        x => x.distance === 1000,
                      ).price,
                    )}`}
                  </Text>
                </InfoColumn>
              </InfoRow>
            </InfoWrapper>
          </Section>
          <Section print={print}>
            <TitleWrapper print={print}>
              <IconWrapper print={print}>
                <Building
                  width={print ? 11 : null}
                  height={print ? 12 : null}
                />
              </IconWrapper>
              <SectionTitle>Características do imóvel</SectionTitle>
            </TitleWrapper>
            <InfoWrapper print={print}>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Dormitórios
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text bold={print} print={print}>
                    {valuation && valuation.main_bedrooms
                      ? valuation && valuation.main_bedrooms
                      : formatValuationParamsString(
                          valuation && valuation.bedrooms,
                        ) || '-'}
                  </Text>
                </InfoColumn>
              </InfoRow>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Suítes
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text print={print}>
                    {valuation && valuation.main_suites
                      ? valuation && valuation.main_suites
                      : formatValuationParamsString(
                          valuation && valuation.suites,
                        ) || '-'}
                  </Text>
                </InfoColumn>
              </InfoRow>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Garagens
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text print={print}>
                    {valuation && valuation.main_garages
                      ? valuation && valuation.main_garages
                      : formatValuationParamsString(
                          valuation && valuation.garages,
                        ) || '-'}
                  </Text>
                </InfoColumn>
              </InfoRow>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Área
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text bold={print} print={print}>
                    {valuation && valuation.main_area
                      ? valuation && valuation.main_area
                      : (valuation && valuation.area) || '-'}{' '}
                    m²
                  </Text>
                </InfoColumn>
              </InfoRow>
              <InfoRow print={print}>
                <InfoColumn>
                  <Text bold dark title print={print}>
                    Valor por m²
                  </Text>
                </InfoColumn>
                <InfoColumn>
                  <Text bold={print} print={print}>
                    R$ {formatToLocale(valuation.suggested_meter_price)}
                  </Text>
                </InfoColumn>
              </InfoRow>
            </InfoWrapper>
          </Section>
        </DataSection>
        <Wrapper>
          <TimeMedianContainer print={print}>
            <TimeWrapper print={print}>
              <Text dark>
                Mediana do tempo dos anúncios com as mesmas características:
              </Text>
              <Time print={print}>{valuation.liquidity_reference} dias</Time>
            </TimeWrapper>
            <MeasuresContainer print={print}>
              <div>
                <Measure print={print} color="#B18935">
                  {parseInt(valuation.region_liquidity[0].value, 0)}
                </Measure>
                <Needle print={print} />
                <Text>Excelente</Text>
              </div>
              <Measure print={print} color="#9F7F3B" />
              <div>
                <Measure print={print} color="#8D7542">
                  {parseInt(valuation.region_liquidity[2].value, 0)}
                </Measure>
                <Needle print={print} />
                <Text>Bom</Text>
              </div>
              <div>
                <Measure print={print} color="#7B6C48">
                  {parseInt(valuation.region_liquidity[3].value, 0)}
                </Measure>
                <Needle />
                <Text>Aceitável</Text>
              </div>
              <Measure print={print} color="#69624E" />
              <Measure print={print} color="#575855" />
              <div>
                <Measure print={print} color="#454E5B">
                  {`> ${parseInt(valuation.region_liquidity[5].value, 0)}`}
                </Measure>
                <Needle />
                <Text>Insatisfatório</Text>
              </div>
            </MeasuresContainer>
          </TimeMedianContainer>
          <RatingWrapper print={print}>
            <RatingBody print={print}>
              <RatingIconWrapper print={print}>
                <Rating height={print ? 55 : 98.52} width={print ? 50 : 110} />
              </RatingIconWrapper>
              <div>
                <RatingHeader print={print}>Rating EEmovel</RatingHeader>
                <RatingRank print={print}>{valuation.region_rating}</RatingRank>
              </div>
            </RatingBody>
            <RatingFooter print={print}>
              <Text
                textFont={`${print ? '12px/14px' : '14px/17px'} Barlow`}
                bold
                gold
              >
                Estimativa EEmovel
              </Text>
              <SuggestedPriceContainer>
                {(() => {
                  const suggPrice = formatToLocale(
                    valuation.suggested_price,
                  ).split(',')
                  return (
                    <Fragment>
                      <Text
                        textFont={`${print ? '12px/14px' : '14px/17px'} Barlow`}
                        gold
                      >
                        R$
                      </Text>
                      <Text
                        textFont={`${
                          print ? '18px/19.5px' : '30px/22.5px'
                        } Barlow`}
                        bold
                        gold
                      >
                        {suggPrice[0]},
                      </Text>
                      <Text
                        textFont={`${print ? '10px/12px' : '12px/14px'} Barlow`}
                        gold
                      >
                        {suggPrice[1]}
                      </Text>
                    </Fragment>
                  )
                })()}
              </SuggestedPriceContainer>
            </RatingFooter>
          </RatingWrapper>
        </Wrapper>
        <Table print={print}>
          <Row header print={print} textCenter>
            <Col>
              <Text table bold={print} print={print}>
                Imobilíaria
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Bairro
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Valor (R$)
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Valor m² (R$)
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                I.S.
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Dorm.
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Garagem
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Área (m²)
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Endereço
              </Text>
            </Col>
            <Col>
              <Text table bold={print} print={print}>
                Idade do Anúncio
              </Text>
            </Col>
          </Row>
          {valuation.ad_sample.map((realty, index) => (
            <Row print={print} key={index} textCenter>
              <Col>
                <Text table print={print} dark>
                  {realty.real_state_agency}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {realty.address.district}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {formatToLocale(realty.price)}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {formatToLocale(realty.sqr_mtr_price)}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {formatToLocale(realty.similarity)}%
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {realty.bedrooms}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {realty.garages}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {realty.area}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {realty.address.street}, {realty.address.number}
                </Text>
              </Col>
              <Col>
                <Text table print={print} dark>
                  {realty.ad_age || '-'} dia(s)
                </Text>
              </Col>
            </Row>
          ))}
        </Table>
      </Container>
    </Main>
  ) : (
    <div>Carregando...</div>
  )
}

export default ValuationReport
